/**
 * Hub-and-spoke SEO landing pages.
 * Logistics delivery: Ahmedabad, Surat, Pune, Mumbai (managed service).
 * Fleet SaaS hubs: pan-India city pages under /hubs/...
 * Fleet features: /fleet-saas/...
 */

import {
  FLEET_SAAS_HUB,
  LOGISTICS_HUB,
  MEDICAL_DELIVERY_SERVICE,
  fleetFeaturePath,
  fleetHubPath,
  logisticsCityPath,
} from './seoUrlManifest';

function logisticsCityPage({ pageKey, slug, city, state, turnaroundHours = 4, localDetail }) {
  const path = logisticsCityPath(slug);
  return {
    pageKey,
    path,
    hub: 'logistics',
    city,
    state,
    serviceType: 'logistics',
    eyebrow: `Medicine Delivery ${city}`,
    eyebrowIcon: 'Truck',
    title: 'Same-Day B2B Medicine Delivery',
    highlight: `in ${city}`,
    subtitle: `FastWhistle managed pharma logistics for medicine distributors in ${city} — same-day delivery to chemists, doctors & stockists with live tracking and per-parcel pricing.`,
    h1: `Medicine Delivery Service in ${city} for Pharma Distributors`,
    answerBlock: `FastWhistle provides same-day B2B medicine delivery in ${city} for pharmaceutical distributors. Managed pharma logistics with FastWhistle riders delivers parcels to chemists, doctors, and stockists in under ${turnaroundHours} hours with live GPS tracking and per-parcel pricing — you do not hire or manage delivery fleet staff.`,
    intro: [
      localDetail ||
        `FastWhistle is a managed medical delivery service for medicine distributors in ${city}, ${state}. You hand over pharma parcels; our riders deliver the same day with proof of delivery and real-time status in your distributor panel.`,
      `This is B2B pharmaceutical logistics — not consumer home medicine delivery. Pricing is per parcel with no fleet salaries, vehicles, or HR overhead on your side.`,
    ],
    features: [
      { icon: 'Truck', title: `Same-Day ${city} Delivery`, detail: `Pharma parcels to retail pharmacies, clinics, and stockists across ${city}.` },
      { icon: 'MapTrifold', title: 'Live Parcel Tracking', detail: 'Real-time pickup, transit, and delivered status for every parcel.' },
      { icon: 'CurrencyInr', title: 'Per-Parcel Pricing', detail: 'Transparent per-delivery charges — scale volume without fixed fleet cost.' },
      { icon: 'Buildings', title: 'Distributor-First', detail: 'Built for pharma wholesalers and distributors, not generic couriers.' },
    ],
    faq: [
      {
        question: `Which areas in ${city} does FastWhistle cover?`,
        answer: `FastWhistle managed medicine delivery covers ${city} and nearby distributor delivery zones. Contact us with your party list and routes for zone confirmation.`,
      },
      {
        question: `How fast is medicine delivery in ${city}?`,
        answer: `Typical same-day turnaround for distributor parcels in ${city} is under ${turnaroundHours} hours from handover to delivery completion, subject to cut-off times and volume.`,
      },
      {
        question: 'Is this for patients or distributors?',
        answer: 'FastWhistle serves pharmaceutical distributors delivering to chemists, doctors, and stockists — B2B medical delivery only.',
      },
    ],
    ctaInterest: 'logistics',
    whatsappText: `Hi, I need medicine delivery service in ${city} for my distributor.`,
    relatedLinks: [
      { label: 'All logistics cities', to: LOGISTICS_HUB },
      { label: 'Fleet SaaS (Pan-India)', to: FLEET_SAAS_HUB },
      { label: 'Contact Us', to: '/contact-us' },
    ],
  };
}

function fleetHubPage({ pageKey, slug, city, state, hq = false }) {
  const path = fleetHubPath(slug);
  return {
    pageKey,
    path,
    hub: 'fleet-saas',
    city,
    state,
    serviceType: 'fleet',
    eyebrow: `Pharma Fleet SaaS ${city}`,
    eyebrowIcon: 'DeviceMobile',
    title: 'Pharma Fleet Management Software',
    highlight: hq ? `— ${city} HQ, Pan-India` : `for ${city}`,
    subtitle: `FastWhistle Fleet SaaS helps medicine distributors in ${city} and across India manage salesmen, riders, and field staff — tasks, live GPS, attendance, collections, and accounting sync.`,
    h1: hq
      ? `Best Pharma Fleet Management Software from ${city} — Available Pan-India`
      : `Pharma Fleet Management Software for Distributors in ${city}`,
    answerBlock: hq
      ? `FastWhistle Fleet SaaS is pharmaceutical field-force software headquartered in Surat, available pan-India. Distributors track salesmen and riders with live GPS, attendance, TADA, collection tasks, and ZeroMDS accounting sync — one Android app and web panel for teams in any Indian city.`
      : `FastWhistle Fleet SaaS is pan-India pharma fleet software for medicine distributors in ${city} and across ${state}. Manage your own field staff with live GPS tracking, attendance, order and collection tasks, and accounting sync — not outsourced delivery riders.`,
    intro: [
      `Searching for pharma fleet management software in ${city}? FastWhistle Fleet SaaS gives distributors a mobile app and web panel to run their own salesmen and delivery staff anywhere in India.`,
      hq
        ? 'FastWhistle is headquartered in Surat, Gujarat (5INCH VENTURES PRIVATE LIMITED). Fleet SaaS onboarding and support are remote — your team can go live from Mumbai, Delhi, Bengaluru, Indore, or any Indian city.'
        : `Fleet SaaS is separate from FastWhistle managed Logistics (delivery riders). Use Fleet SaaS when you employ your own field team; use Logistics when you want same-day delivery in Ahmedabad, Surat, Pune, or Mumbai.`,
    ],
    features: [
      { icon: 'MapPin', title: 'Live GPS Tracking', detail: 'Real-time location and day-wise travel history for every field employee.' },
      { icon: 'ListChecks', title: 'Tasks & Collections', detail: 'Order tasks, collection tasks, and calling workflows from one panel.' },
      { icon: 'Fingerprint', title: 'Attendance & TADA', detail: 'Attendance, leave, TADA, payroll hooks, and daily expenses.' },
      { icon: 'ArrowsClockwise', title: 'ZeroMDS Sync', detail: 'Sync parties, products, orders, and outstandings from compatible accounting software.' },
    ],
    faq: [
      {
        question: `Is FastWhistle Fleet SaaS available in ${city}?`,
        answer: `Yes. Fleet SaaS is pan-India — distributors in ${city} and every Indian city can onboard field staff. Managed medicine delivery (Logistics) is separate and live in Ahmedabad, Surat, Pune, and Mumbai only.`,
      },
      {
        question: 'What is the difference between Fleet SaaS and Logistics?',
        answer: 'Fleet SaaS is software for your employees. Logistics is managed same-day delivery with FastWhistle riders in four cities.',
      },
    ],
    ctaInterest: 'fleet',
    whatsappText: `Hi, I want FastWhistle Fleet SaaS for my distributor team in ${city}.`,
    relatedLinks: [
      { label: 'Fleet SaaS overview', to: FLEET_SAAS_HUB },
      { label: 'Salesman tracking app', to: fleetFeaturePath('pharma-salesman-tracking-app') },
      { label: 'Managed delivery cities', to: LOGISTICS_HUB },
    ],
  };
}

function fleetFeaturePage({ pageKey, slug, title, highlight, answerBlock, intro, features, faq, whatsappText }) {
  return {
    pageKey,
    path: fleetFeaturePath(slug),
    hub: 'fleet-saas',
    city: null,
    serviceType: 'fleet-feature',
    eyebrow: 'Fleet SaaS Feature',
    eyebrowIcon: 'Lightning',
    title,
    highlight,
    subtitle: answerBlock,
    h1: `${title} ${highlight}`.trim(),
    answerBlock,
    intro,
    features,
    faq,
    ctaInterest: 'fleet',
    whatsappText,
    relatedLinks: [
      { label: 'Fleet SaaS overview', to: FLEET_SAAS_HUB },
      { label: 'Pan-India hubs', to: fleetHubPath('mumbai') },
      { label: 'Contact sales', to: '/contact-us' },
    ],
  };
}

const logisticsPages = [
  logisticsCityPage({
    pageKey: 'logisticsSurat',
    slug: 'surat',
    city: 'Surat',
    state: 'Gujarat',
    localDetail:
      'FastWhistle operates managed medicine delivery from Vesu, Surat — same-day pharma parcel runs for distributors across Surat city and nearby zones.',
  }),
  logisticsCityPage({
    pageKey: 'logisticsAhmedabad',
    slug: 'ahmedabad',
    city: 'Ahmedabad',
    state: 'Gujarat',
    localDetail:
      'FastWhistle runs managed medicine delivery for Ahmedabad distributors — parcels handed to FastWhistle riders reach retail pharmacies, doctors, and stockists across the city the same day, tracked live from your distributor panel.',
  }),
  logisticsCityPage({
    pageKey: 'logisticsPune',
    slug: 'pune',
    city: 'Pune',
    state: 'Maharashtra',
    localDetail:
      'FastWhistle provides managed B2B medicine delivery for pharma distributors in Pune — hand off parcels to FastWhistle riders and track same-day delivery status from your distributor panel.',
  }),
  logisticsCityPage({
    pageKey: 'logisticsMumbai',
    slug: 'mumbai',
    city: 'Mumbai',
    state: 'Maharashtra',
    turnaroundHours: 5,
    localDetail:
      'FastWhistle provides managed B2B medicine delivery for pharma distributors across Mumbai and Navi Mumbai — hand off parcels to FastWhistle riders and track same-day delivery status from your distributor panel.',
  }),
];

const fleetHubPages = [
  fleetHubPage({ pageKey: 'fleetHubSurat', slug: 'surat', city: 'Surat', state: 'Gujarat', hq: true }),
  fleetHubPage({ pageKey: 'fleetHubMumbai', slug: 'mumbai', city: 'Mumbai', state: 'Maharashtra' }),
  fleetHubPage({ pageKey: 'fleetHubDelhiNcr', slug: 'delhi-ncr', city: 'Delhi NCR', state: 'Delhi NCR' }),
  fleetHubPage({ pageKey: 'fleetHubBengaluru', slug: 'bengaluru', city: 'Bengaluru', state: 'Karnataka' }),
  fleetHubPage({ pageKey: 'fleetHubIndore', slug: 'indore', city: 'Indore', state: 'Madhya Pradesh' }),
];

const fleetFeaturePages = [
  fleetFeaturePage({
    pageKey: 'fleetFeatureSalesmanTracking',
    slug: 'pharma-salesman-tracking-app',
    title: 'Pharma Salesman',
    highlight: 'Tracking App',
    answerBlock:
      'FastWhistle Fleet SaaS provides live GPS tracking for pharma salesmen and delivery riders pan-India. Distributors see real-time location, day-wise route history, and task completion from one panel — built for medicine wholesale field teams, not generic fleet tools.',
    intro: [
      'Track pharmaceutical field staff with purpose-built GPS — not adapted from generic logistics software.',
      'Assign order, collection, and visit tasks; verify route adherence and day-wise kilometres from the distributor panel.',
    ],
    features: [
      { icon: 'MapPin', title: 'Live location', detail: 'Real-time GPS on map with last-seen timestamp.' },
      { icon: 'Path', title: 'Route history', detail: 'Day-wise travel log for audits and TADA validation.' },
      { icon: 'ListChecks', title: 'Task proof', detail: 'Link visits and deliveries to location checkpoints.' },
      { icon: 'DeviceMobile', title: 'Android fleet app', detail: 'Field staff use the FastWhistle Fleet mobile app.' },
    ],
    faq: [
      {
        question: 'Does the tracking app work outside Gujarat?',
        answer: 'Yes. Fleet SaaS and live GPS tracking are available pan-India for medicine distributors.',
      },
    ],
    whatsappText: 'Hi, I need a pharma salesman tracking app for my distributor field team.',
  }),
  fleetFeaturePage({
    pageKey: 'fleetFeatureAttendanceTada',
    slug: 'pharma-field-attendance-tada',
    title: 'Pharma Field Attendance',
    highlight: '& TADA',
    answerBlock:
      'FastWhistle Fleet SaaS handles pharma field attendance, leave, and TADA (travel allowance) for salesmen and riders. Distributors digitise duty on/off, approve expenses, and export payroll-ready summaries — integrated with live GPS for verification pan-India.',
    intro: [
      'Replace paper attendance and manual TADA sheets with GPS-verified duty marks and structured expense capture.',
      'Designed for medicine distributor field teams with high daily travel volumes.',
    ],
    features: [
      { icon: 'Fingerprint', title: 'Duty on/off', detail: 'GPS-backed attendance marks from the fleet app.' },
      { icon: 'Receipt', title: 'TADA capture', detail: 'Structured travel allowance entries with approval flow.' },
      { icon: 'CalendarBlank', title: 'Leave management', detail: 'Leave requests and balances for field staff.' },
      { icon: 'Export', title: 'Payroll export', detail: 'Summaries for HR and accounts teams.' },
    ],
    faq: [
      {
        question: 'Can TADA be validated with GPS?',
        answer: 'Yes. Attendance and travel logs use live location data to support TADA verification workflows.',
      },
    ],
    whatsappText: 'Hi, I want attendance and TADA management for my pharma field team.',
  }),
  fleetFeaturePage({
    pageKey: 'fleetFeatureCollections',
    slug: 'pharma-collections-management',
    title: 'Pharma Collections',
    highlight: 'Management',
    answerBlock:
      'FastWhistle Fleet SaaS tracks outstanding collections for pharma distributors pan-India. Assign collection tasks to field staff, record payments against party ledgers, and sync results with your accounting system — reducing DSO and manual follow-up calls.',
    intro: [
      'Collection tasks with party-wise outstanding visibility for salesmen and dedicated collection staff.',
      'Proof of visit, payment mode, and partial collection handling built for wholesale pharma workflows.',
    ],
    features: [
      { icon: 'CurrencyInr', title: 'Outstanding view', detail: 'Party-wise dues visible to assigned field staff.' },
      { icon: 'ListChecks', title: 'Collection tasks', detail: 'Route-planned collection visits with status tracking.' },
      { icon: 'ArrowsClockwise', title: 'Ledger sync', detail: 'Post collections back to compatible accounting software.' },
      { icon: 'ChartLine', title: 'DSO insights', detail: 'Track collection performance by employee and route.' },
    ],
    faq: [
      {
        question: 'Does it integrate with distributor accounting?',
        answer: 'Yes. FastWhistle supports sync with ZeroMDS and compatible distributor accounting platforms.',
      },
    ],
    whatsappText: 'Hi, I need pharma collections management software for my field team.',
  }),
  fleetFeaturePage({
    pageKey: 'fleetFeatureZeroMds',
    slug: 'zeromds-accounting-sync',
    title: 'ZeroMDS',
    highlight: 'Accounting Sync',
    answerBlock:
      'FastWhistle Fleet SaaS syncs with ZeroMDS and compatible pharma distributor accounting software pan-India. Parties, products, orders, stock, and outstandings flow between your accounts system and the fleet app — eliminating double entry for field operations.',
    intro: [
      'Purpose-built integration for Indian medicine distributors running ZeroMDS or similar wholesale accounting stacks.',
      'Field tasks, collections, and order updates reflect back to your master data automatically.',
    ],
    features: [
      { icon: 'Plugs', title: 'Master sync', detail: 'Parties, products, and stock from accounting to fleet app.' },
      { icon: 'Package', title: 'Order flow', detail: 'Field-captured orders push to your distributor system.' },
      { icon: 'CurrencyInr', title: 'Outstanding sync', detail: 'Live dues for collection task assignment.' },
      { icon: 'ArrowsClockwise', title: 'Two-way updates', detail: 'Reduce manual re-entry between desk and field.' },
    ],
    faq: [
      {
        question: 'Which accounting software is supported?',
        answer: 'ZeroMDS is a primary integration. Contact us for compatibility with your current distributor accounting stack.',
      },
    ],
    whatsappText: 'Hi, I want ZeroMDS integration with my fleet and field staff app.',
  }),
];

const overviewPages = [
  {
    pageKey: 'medicalDeliveryService',
    path: MEDICAL_DELIVERY_SERVICE,
    hub: 'logistics',
    city: null,
    serviceType: 'logistics',
    eyebrow: 'Medical Delivery Service',
    eyebrowIcon: 'Package',
    title: 'Medical & Pharma Delivery',
    highlight: 'for Distributors',
    subtitle:
      'FastWhistle B2B medical delivery for medicine distributors — managed same-day logistics in Ahmedabad, Surat, Pune & Mumbai, plus Fleet SaaS pan-India.',
    h1: 'Medical Delivery Service for Pharmaceutical Distributors in India',
    answerBlock:
      'FastWhistle is an Indian B2B pharmaceutical logistics platform. Managed same-day medicine delivery runs in Ahmedabad, Surat, Pune, and Mumbai; Fleet SaaS field-management software is available pan-India for distributors tracking salesmen, attendance, collections, and ZeroMDS accounting sync.',
    intro: [
      'FastWhistle provides medical delivery service tailored for pharmaceutical distributors — not retail consumers.',
      'Choose managed Logistics where FastWhistle riders deliver in four metro cities, or Fleet SaaS to manage your own field team anywhere in India.',
    ],
    features: [
      { icon: 'Truck', title: 'Managed Logistics', detail: 'Same-day delivery in Ahmedabad, Surat, Pune & Mumbai.' },
      { icon: 'DeviceMobile', title: 'Fleet SaaS', detail: 'Pan-India software for your salesmen and riders.' },
      { icon: 'ChartLine', title: 'Collections', detail: 'Field collection tracking synced with masters.' },
      { icon: 'Plugs', title: 'ZeroMDS sync', detail: 'Accounting integration for distributor operations.' },
    ],
    faq: [
      {
        question: 'Which cities have managed medicine delivery?',
        answer: 'Managed same-day B2B medicine delivery is live in Ahmedabad, Surat, Pune, and Mumbai. Fleet SaaS is pan-India.',
      },
      {
        question: 'Does FastWhistle deliver to patient homes?',
        answer: 'No. FastWhistle serves pharmaceutical distributors delivering to chemists, doctors, and stockists.',
      },
    ],
    ctaInterest: 'both',
    whatsappText: 'Hi, I want to know about FastWhistle medical delivery for distributors.',
    relatedLinks: [
      { label: 'Logistics hub', to: LOGISTICS_HUB },
      { label: 'Fleet SaaS hub', to: FLEET_SAAS_HUB },
      { label: 'Compare products', to: '/compare/logistics-vs-fleet-saas' },
    ],
  },
];

export const seoLandingPages = [
  ...logisticsPages,
  ...fleetHubPages,
  ...fleetFeaturePages,
  ...overviewPages,
];

export const landingPageByPath = Object.fromEntries(seoLandingPages.map((page) => [page.path, page]));

export const landingPageByKey = Object.fromEntries(seoLandingPages.map((page) => [page.pageKey, page]));

/** All paths including legacy keys for redirect lookup */
export const legacyPathToCanonical = {
  '/medicine-delivery-surat': logisticsCityPath('surat'),
  '/medicine-delivery-ahmedabad': logisticsCityPath('ahmedabad'),
  '/fleet-management-surat': fleetHubPath('surat'),
};
